import React, { useState } from "react";
import axios from "axios";
import { MyDomain } from "../../App";
import "react-phone-input-2/lib/style.css";
import PhoneInput from "react-phone-input-2";
import { useTranslation } from "react-i18next";
import { useAppContext } from "../../AppContext";
import {
  Dialog,
  DialogContent,
  IconButton,
  Box,
  Typography,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import Bokahitlogo2nobgc from "../../Images/Bokahitlogo2nobgc.png";

const C = {
  primary: "#1A1A2E",
  primaryHover: "#16213E",
  accent: "#C9A84C",
  accentLight: "#FDF6E3",
  accentMuted: "rgba(201,168,76,0.12)",
  success: "#10b981",
  border: "#e5e7eb",
  textMuted: "#6b7280",
  textLight: "#9ca3af",
  disabled: "#d1d5db",
};

export default function MPLoginWithPhoneNumberModal({ open, onClose }) {
  const { t } = useTranslation();
  const { setUser } = useAppContext();
  const [authStep, setAuthStep] = useState(1);
  const [otp, setOtp] = useState("");
  const [message, setMessage] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [countryCode, setCountryCode] = useState("se");
  const [callingCode, setCallingCode] = useState("+46");
  const [userName, setUserName] = useState("");
  const [loading, setLoading] = useState(false);

  const handleClose = () => {
    setAuthStep(1);
    setOtp("");
    setMessage("");
    setPhoneNumber("");
    setUserName("");
    setLoading(false);
    onClose();
  };

  const sendOTP = async () => {
    if (!phoneNumber || phoneNumber.length < 7) {
      setMessage(t("PleaseEnterValidPhone"));
      return;
    }
    setMessage("");
    setLoading(true);
    try {
      let cleanedPhone = phoneNumber;
      if (cleanedPhone[0] === "0") cleanedPhone = cleanedPhone.slice(1);
      const fullPhoneNumber = callingCode + cleanedPhone;
      const response = await axios.post(`${MyDomain}Otp/send`, {
        Phone: fullPhoneNumber,
      });
      if (response.data.success) {
        setMessage(t("OTPSentTo") + " " + fullPhoneNumber);
        setAuthStep(2);
      } else {
        throw new Error(response.data.message || "Failed to send OTP");
      }
    } catch (error) {
      let errorMessage = t("ErrorMessage");
      if (error.response?.data?.message)
        errorMessage = error.response.data.message;
      else if (error.message) errorMessage = error.message;
      setMessage(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const verifyOTP = async () => {
    if (!otp || otp.length < 6) {
      setMessage(t("PleaseEnterValidOTP"));
      return;
    }
    setLoading(true);
    try {
      let cleanedPhone = phoneNumber;
      if (cleanedPhone[0] === "0") cleanedPhone = cleanedPhone.slice(1);
      const fullPhoneNumber = callingCode + cleanedPhone;
      const response = await axios.post(`${MyDomain}Otp/verify`, {
        Phone: fullPhoneNumber,
        Otp: otp,
      });
      if (response.data.verified) {
        setMessage(t("PhoneVerifiedSuccessfully"));
        setAuthStep(3);
      } else {
        throw new Error("Invalid verification code");
      }
    } catch (err) {
      setMessage(err.response?.data?.message || t("InvalidOTP"));
    } finally {
      setLoading(false);
    }
  };

  const resendOTP = async () => {
    setOtp("");
    setMessage("");
    await sendOTP();
  };

  const saveUserToBackend = async () => {
    if (!userName.trim()) {
      setMessage(t("PleaseEnterYourName"));
      return;
    }
    setLoading(true);
    try {
      let cleanedPhone = phoneNumber;
      if (cleanedPhone[0] === "0") cleanedPhone = cleanedPhone.slice(1);
      const data = {
        CountryNumber: callingCode,
        PhoneNumber: cleanedPhone.startsWith("0")
          ? cleanedPhone
          : "0" + cleanedPhone,
        Name: userName,
      };
      const response = await axios.post(
        `${MyDomain}Users2/UserLoginWithPhoneNumber`,
        data,
        {
          headers: { "Content-Type": "application/json" },
        },
      );
      localStorage.setItem("user", JSON.stringify(response.data));
      setUser(response.data);
      setMessage(t("LoginSuccessful"));
      setTimeout(() => handleClose(), 500);
    } catch (err) {
      setMessage(
        t("FailedToSaveUser") + " " + (err.response?.data || err.message),
      );
    } finally {
      setLoading(false);
    }
  };

  const goBackToPhoneEntry = () => {
    setAuthStep(1);
    setOtp("");
    setMessage("");
  };

  const isError = (msg) =>
    msg.includes(t("ErrorMessage")) ||
    msg.includes(t("InvalidOTP")) ||
    msg.includes(t("FailedToSaveUser")) ||
    msg.includes("Failed") ||
    msg.includes("Invalid");

  const StepDot = ({ num, targetStep }) => {
    const active = authStep === targetStep;
    const done = authStep > targetStep;
    const stepLabels = { 1: t("Phone"), 2: t("Verify"), 3: t("Name") };
    return (
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <div
          style={{
            width: "30px",
            height: "30px",
            borderRadius: "50%",
            backgroundColor: done ? C.success : active ? C.accent : C.border,
            color: done || active ? C.primary : C.textLight,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: "700",
            fontSize: "13px",
            transition: "all 0.3s ease",
          }}
        >
          {done ? "✓" : num}
        </div>
        <span
          style={{
            fontSize: "13px",
            fontWeight: active ? "700" : "500",
            color: done ? C.success : active ? C.accent : C.textLight,
          }}
        >
          {stepLabels[num]}
        </span>
      </div>
    );
  };

  const Connector = ({ passed }) => (
    <div
      style={{
        width: "28px",
        height: "2px",
        backgroundColor: passed ? C.success : C.border,
        transition: "background-color 0.3s ease",
      }}
    />
  );

  const MessageBox = ({ msg }) =>
    msg ? (
      <div
        style={{
          padding: "11px 14px",
          borderRadius: "10px",
          backgroundColor: isError(msg) ? "#fee2e2" : "#d1fae5",
          color: isError(msg) ? "#991b1b" : "#065f46",
          fontSize: "13px",
          textAlign: "center",
          fontWeight: "500",
        }}
      >
        {msg}
      </div>
    ) : null;

  const PrimaryBtn = ({ onClick, disabled, label }) => (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        width: "100%",
        padding: "15px",
        fontSize: "15px",
        fontWeight: "700",
        border: "none",
        borderRadius: "12px",
        backgroundColor: disabled ? C.disabled : C.primary,
        color: disabled ? "#fff" : C.accent,
        cursor: disabled ? "not-allowed" : "pointer",
        boxShadow: disabled ? "none" : "0 4px 16px rgba(26,26,46,0.3)",
        transition: "all 0.3s ease",
        outline: "none",
      }}
      onMouseEnter={(e) => {
        if (!disabled) {
          e.currentTarget.style.backgroundColor = C.primaryHover;
          e.currentTarget.style.transform = "translateY(-1px)";
          e.currentTarget.style.boxShadow = "0 6px 20px rgba(26,26,46,0.4)";
        }
      }}
      onMouseLeave={(e) => {
        if (!disabled) {
          e.currentTarget.style.backgroundColor = C.primary;
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.boxShadow = "0 4px 16px rgba(26,26,46,0.3)";
        }
      }}
    >
      {label}
    </button>
  );

  const SecondaryBtn = ({ onClick, disabled, label }) => (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        width: "100%",
        padding: "13px",
        fontSize: "14px",
        fontWeight: "600",
        border: `1.5px solid ${C.accent}`,
        borderRadius: "12px",
        backgroundColor: "transparent",
        color: C.accent,
        cursor: disabled ? "not-allowed" : "pointer",
        transition: "all 0.25s ease",
        outline: "none",
      }}
      onMouseEnter={(e) => {
        if (!disabled) e.currentTarget.style.backgroundColor = C.accentMuted;
      }}
      onMouseLeave={(e) => {
        if (!disabled) e.currentTarget.style.backgroundColor = "transparent";
      }}
    >
      {label}
    </button>
  );

  const GhostBtn = ({ onClick, label }) => (
    <button
      onClick={onClick}
      style={{
        width: "100%",
        padding: "13px",
        fontSize: "14px",
        fontWeight: "600",
        border: `1px solid ${C.border}`,
        borderRadius: "12px",
        backgroundColor: "#fff",
        color: C.textMuted,
        cursor: "pointer",
        transition: "all 0.25s ease",
        outline: "none",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = C.accent;
        e.currentTarget.style.color = C.accent;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = C.border;
        e.currentTarget.style.color = C.textMuted;
      }}
    >
      {label}
    </button>
  );

  const inputStyle = {
    width: "100%",
    height: "50px",
    fontSize: "15px",
    paddingLeft: "14px",
    paddingRight: "14px",
    border: `2px solid ${C.border}`,
    borderRadius: "12px",
    outline: "none",
    transition: "border-color 0.25s ease",
    color: C.primary,
    boxSizing: "border-box",
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          position: "relative",
          maxWidth: { xs: "92%", sm: "480px" },
          margin: "auto",
          overflow: "hidden",
        },
      }}
    >
      {/* Gold top accent bar */}
      <div
        style={{
          height: "4px",
          background: `linear-gradient(90deg, ${C.primary} 0%, ${C.accent} 100%)`,
        }}
      />

      <IconButton
        onClick={handleClose}
        sx={{
          position: "absolute",
          right: 10,
          top: 14,
          color: C.textMuted,
          zIndex: 1,
          "&:hover": {
            backgroundColor: "rgba(201,168,76,0.1)",
            color: C.accent,
          },
        }}
      >
        <Close />
      </IconButton>

      <DialogContent sx={{ p: { xs: 3, sm: 4 }, pt: { xs: 3, sm: 4 } }}>
        {/* Brand */}
        <Box sx={{ textAlign: "center", mb: 3 }}>
          <img
            src={Bokahitlogo2nobgc}
            alt="Bokahit"
            style={{
              height: "36px",
              objectFit: "contain",
              marginBottom: "8px",
            }}
          />
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: "13px" }}
          >
            {t("SignInToContinue") || "Sign in to continue"}
          </Typography>
        </Box>

        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          {/* Progress stepper */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "10px",
              padding: "12px 0",
              flexWrap: "wrap",
            }}
          >
            <StepDot num={1} targetStep={1} />
            <Connector passed={authStep > 1} />
            <StepDot num={2} targetStep={2} />
            <Connector passed={authStep > 2} />
            <StepDot num={3} targetStep={3} />
          </div>

          {/* STEP 1 */}
          {authStep === 1 && (
            <div
              style={{ display: "flex", flexDirection: "column", gap: "18px" }}
            >
              <div>
                <h3
                  style={{
                    fontSize: "20px",
                    fontWeight: "700",
                    color: C.primary,
                    marginBottom: "6px",
                    textAlign: "center",
                  }}
                >
                  {t("Welcome")}
                </h3>
                <p
                  style={{
                    fontSize: "13px",
                    color: C.textMuted,
                    textAlign: "center",
                    margin: 0,
                  }}
                >
                  {t("EnterPhoneToGetStarted")}
                </p>
              </div>

              <div>
                <label
                  style={{
                    fontWeight: "600",
                    fontSize: "13px",
                    color: C.primary,
                    marginBottom: "8px",
                    display: "block",
                  }}
                >
                  {t("PhoneNumber")}
                </label>
                <div
                  style={{
                    display: "flex",
                    gap: "10px",
                    alignItems: "stretch",
                    marginTop: "6px",
                  }}
                >
                  <div style={{ minWidth: "105px" }}>
                    <PhoneInput
                      country={countryCode}
                      value=""
                      onChange={(value, country) => {
                        setCountryCode(country.countryCode);
                        setCallingCode("+" + country.dialCode);
                      }}
                      enableSearch
                      inputStyle={{ display: "none" }}
                      buttonStyle={{
                        width: "100%",
                        height: "50px",
                        borderRadius: "12px",
                        border: `2px solid ${C.border}`,
                        backgroundColor: "#fff",
                      }}
                      containerStyle={{ width: "100%" }}
                    />
                    <div
                      style={{
                        marginTop: "5px",
                        textAlign: "center",
                        fontWeight: "700",
                        fontSize: "13px",
                        color: C.accent,
                      }}
                    >
                      {callingCode}
                    </div>
                  </div>
                  <input
                    type="tel"
                    placeholder="50-123-4567"
                    value={phoneNumber}
                    onChange={(e) =>
                      setPhoneNumber(e.target.value.replace(/[^0-9]/g, ""))
                    }
                    onKeyPress={(e) => e.key === "Enter" && sendOTP()}
                    style={inputStyle}
                    onFocus={(e) => (e.target.style.borderColor = C.accent)}
                    onBlur={(e) => (e.target.style.borderColor = C.border)}
                  />
                </div>
                <p
                  style={{
                    marginTop: "10px",
                    fontSize: "13px",
                    color: C.textMuted,
                    padding: "10px 12px",
                    backgroundColor: C.accentLight,
                    borderRadius: "8px",
                    border: `1px solid rgba(201,168,76,0.2)`,
                  }}
                >
                  {t("FullNumber")}{" "}
                  <strong style={{ color: C.primary }}>
                    {callingCode + phoneNumber}
                  </strong>
                </p>
              </div>

              <MessageBox msg={message} />
              <PrimaryBtn
                onClick={sendOTP}
                disabled={loading || !phoneNumber}
                label={loading ? t("Sending") : t("SendVerificationCode")}
              />
            </div>
          )}

          {/* STEP 2 */}
          {authStep === 2 && (
            <div
              style={{ display: "flex", flexDirection: "column", gap: "18px" }}
            >
              <div>
                <h3
                  style={{
                    fontSize: "20px",
                    fontWeight: "700",
                    color: C.primary,
                    marginBottom: "6px",
                    textAlign: "center",
                  }}
                >
                  {t("VerifyYourNumber")}
                </h3>
                <p
                  style={{
                    fontSize: "13px",
                    color: C.textMuted,
                    textAlign: "center",
                    margin: 0,
                  }}
                >
                  {t("WeSentCodeTo")}{" "}
                  <strong style={{ color: C.primary }}>
                    {callingCode + phoneNumber}
                  </strong>
                </p>
              </div>

              <div>
                <label
                  style={{
                    fontWeight: "600",
                    fontSize: "13px",
                    color: C.primary,
                    marginBottom: "8px",
                    display: "block",
                  }}
                >
                  {t("VerificationCode")}
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="\d*"
                  placeholder={t("EnterSixDigitCode")}
                  value={otp}
                  onChange={(e) =>
                    setOtp(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))
                  }
                  onKeyPress={(e) => e.key === "Enter" && verifyOTP()}
                  maxLength={6}
                  style={{
                    ...inputStyle,
                    height: "58px",
                    fontSize: "22px",
                    textAlign: "center",
                    letterSpacing: "10px",
                    fontWeight: "700",
                  }}
                  onFocus={(e) => (e.target.style.borderColor = C.accent)}
                  onBlur={(e) => (e.target.style.borderColor = C.border)}
                />
              </div>

              <MessageBox msg={message} />
              <div
                style={{ display: "flex", flexDirection: "column", gap: "8px" }}
              >
                <PrimaryBtn
                  onClick={verifyOTP}
                  disabled={loading || otp.length < 6}
                  label={loading ? t("Verifying") : t("VerifyCode")}
                />
                <SecondaryBtn
                  onClick={resendOTP}
                  disabled={loading}
                  label={
                    loading ? t("Sending") : t("ResendCode") || "Resend Code"
                  }
                />
                <GhostBtn
                  onClick={goBackToPhoneEntry}
                  label={t("ChangePhoneNumber")}
                />
              </div>
            </div>
          )}

          {/* STEP 3 */}
          {authStep === 3 && (
            <div
              style={{ display: "flex", flexDirection: "column", gap: "18px" }}
            >
              <div>
                <h3
                  style={{
                    fontSize: "20px",
                    fontWeight: "700",
                    color: C.primary,
                    marginBottom: "6px",
                    textAlign: "center",
                  }}
                >
                  {t("AlmostThere")}
                </h3>
                <p
                  style={{
                    fontSize: "13px",
                    color: C.textMuted,
                    textAlign: "center",
                    margin: 0,
                  }}
                >
                  {t("EnterNameToComplete")}
                </p>
              </div>

              <div>
                <label
                  style={{
                    fontWeight: "600",
                    fontSize: "13px",
                    color: C.primary,
                    marginBottom: "8px",
                    display: "block",
                  }}
                >
                  {t("YourName")}
                </label>
                <input
                  type="text"
                  placeholder={t("EnterFullName")}
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && saveUserToBackend()}
                  style={inputStyle}
                  onFocus={(e) => (e.target.style.borderColor = C.accent)}
                  onBlur={(e) => (e.target.style.borderColor = C.border)}
                />
              </div>

              <MessageBox msg={message} />
              <PrimaryBtn
                onClick={saveUserToBackend}
                disabled={loading || !userName.trim()}
                label={loading ? t("Completing") : t("CompleteRegistration")}
              />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
